import { pluralizeWord } from "@certa/common/utils/pluralize";

import type { CreateDataObjectWorkflowRelationActionsModelReturn } from "@certa/queries/types/dataObjectsAPIs.types";
import { useKinds } from "@certa/queries/hooks/kinds.hooks";

import type { Kind } from "@certa/types";
import type {
  InitializerObjectHierarchy,
  ObjectCreatorHierarchy,
  ObjectWorkflowCreationType
} from "../StartWorkflowFromObjectDialog.types";
import { useHierarchyNavigation } from "./useObjectHierarchyNavigation";
import { useCallback, useMemo } from "react";

type UseStartWorkflowFromObjectHeirarchyProps = {
  dataObjectWorkflowRelations: CreateDataObjectWorkflowRelationActionsModelReturn;
  onCreateWorkflow: (kind: Kind, objectId?: string) => void;
  preSelectedObjectHierarchy?: {
    objectTypeTag: string;
    objectInstanceId: string;
    objectInstanceUid: string;
  }[];
};

export const useStartWorkflowFromObjectHeirarchy = (
  props: UseStartWorkflowFromObjectHeirarchyProps
) => {
  const {
    dataObjectWorkflowRelations,
    onCreateWorkflow,
    preSelectedObjectHierarchy: preSelectedObjectHierarchyFromProps = []
  } = props;

  const preSelectedObjectHierarchy = preSelectedObjectHierarchyFromProps.map(
    hierarchy => ({
      name: hierarchy.objectTypeTag,
      tag: hierarchy.objectTypeTag
    })
  );

  const { initializerObjectHierarchy, objectCreatorHierarchy } =
    getObjectTypesHierarchies(
      dataObjectWorkflowRelations,
      preSelectedObjectHierarchy
    );

  const {
    state: {
      currentIndex,
      selectedObjectInstanceIdHierarchy,
      selectedObjectInstanceUidHierarchy,
      currentSelectedObjectInstanceId,
      previousSelectedObjectInstanceId,
      previousSelectedObjectInstanceUid,
      previousObjectTypeHierarchy
    },
    actions: { onNext, onPrevious, onSelectObjectInstanceIdHierarchy }
  } = useHierarchyNavigation({
    initializerObjectHierarchy,
    preSelectedObjectHierarchy: preSelectedObjectHierarchyFromProps
  });

  const hasNext = currentIndex < initializerObjectHierarchy.length - 1;
  const hasPrevious = currentIndex > 0;

  const currentObjectTypeInitializerHierarchy =
    initializerObjectHierarchy[currentIndex];

  const currentObjectTypeWorkflowRelation = dataObjectWorkflowRelations?.find(
    relation =>
      relation.relation === "initializer_object" &&
      relation.workflowKindTag ===
        currentObjectTypeInitializerHierarchy.initializerObjectWorkflowKindTag &&
      relation.objectTypeTag === currentObjectTypeInitializerHierarchy.tag
  );

  const { data: kinds } = useKinds();

  const { data: kindsWithCreatorWorkflowPermission } = useKinds({
    allowThirdParty: true
  });

  const getWorkflowKindTag = useCallback(
    (type: ObjectWorkflowCreationType) => {
      const hierarchies =
        type === "createObject"
          ? (objectCreatorHierarchy as ObjectCreatorHierarchy[])
          : (initializerObjectHierarchy as InitializerObjectHierarchy[]);

      const foundHierarchy = hierarchies.find(
        hierarchy => hierarchy.tag === currentObjectTypeInitializerHierarchy.tag
      );

      const workflowKindTag =
        type === "createObject"
          ? (foundHierarchy as ObjectCreatorHierarchy)
              ?.objectCreatorWorkflowKindTag
          : (foundHierarchy as InitializerObjectHierarchy)
              ?.initializerObjectWorkflowKindTag;

      return workflowKindTag;
    },
    [
      currentObjectTypeInitializerHierarchy,
      initializerObjectHierarchy,
      objectCreatorHierarchy
    ]
  );

  const canShowAddSupplierButton = useMemo(() => {
    const workflowKindTag = getWorkflowKindTag("createObject");
    return kindsWithCreatorWorkflowPermission?.some(
      kind => kind.tag === workflowKindTag
    );
  }, [getWorkflowKindTag, kindsWithCreatorWorkflowPermission]);

  const onCreateObjectWorkflow = (type: ObjectWorkflowCreationType) => {
    const workflowKindTag = getWorkflowKindTag(type);

    const workflowKind = kinds?.find(kind => kind.tag === workflowKindTag);

    const objectInstanceId =
      type === "createObject"
        ? previousSelectedObjectInstanceId
        : currentSelectedObjectInstanceId;

    if (workflowKind) {
      onCreateWorkflow(workflowKind, objectInstanceId);
    }
  };

  const currentObjectTypeCreatorHierarchy =
    objectCreatorHierarchy[currentIndex];

  const objectTypesRelationName = pluralizeWord(
    initializerObjectHierarchy?.[currentIndex]?.name || ""
  );

  return {
    canShowAddSupplierButton,
    currentObjectTypeWorkflowRelation,
    objectTypesRelationName,
    selectedObjectInstanceIdHierarchy,
    selectedObjectInstanceUidHierarchy,
    onSelectObjectInstanceIdHierarchy,
    initializerObjectHierarchy,
    objectCreatorHierarchy,
    previousSelectedObjectInstanceId,
    previousSelectedObjectInstanceUid,
    currentSelectedObjectInstanceId,
    previousObjectTypeHierarchy,
    currentObjectTypeInitializerHierarchy,
    currentObjectTypeCreatorHierarchy,
    isLastObjectTypeHierarchy:
      currentIndex === initializerObjectHierarchy.length - 1,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    onCreateObjectWorkflow
  };
};

function getObjectTypesHierarchies(
  dataObjectWorkflowRelations: CreateDataObjectWorkflowRelationActionsModelReturn,
  preSelectedObjectHierarchy: {
    tag: string;
    name: string;
  }[]
) {
  const isNotPreselectedObjectHierarchy = (
    relation: (typeof dataObjectWorkflowRelations)[0]
  ) =>
    !preSelectedObjectHierarchy.find(
      hierarchy => hierarchy.tag === relation.objectTypeTag
    );

  const initializerObjectHierarchy = dataObjectWorkflowRelations
    .filter(
      relation =>
        relation.relation === "initializer_object" &&
        isNotPreselectedObjectHierarchy(relation)
    )
    .map(relation => ({
      name: relation.objectTypeTag as string,
      tag: relation.objectTypeTag as string,
      initializerObjectWorkflowKindTag: relation.workflowKindTag as string
    }))
    .reverse();

  const objectCreatorHierarchy = dataObjectWorkflowRelations
    .filter(
      relation =>
        relation.relation === "object_creator" &&
        isNotPreselectedObjectHierarchy(relation)
    )
    .map(relation => ({
      name: relation.objectTypeTag as string,
      tag: relation.objectTypeTag as string,
      objectCreatorWorkflowKindTag: relation.workflowKindTag as string
    }))
    .reverse();

  return {
    initializerObjectHierarchy,
    objectCreatorHierarchy
  };
}
